import { createSlice, Draft } from '@reduxjs/toolkit';
import { addAsyncCases } from 'shared/utils/redux';
import { Resource } from 'model/Resource';
import { Summary } from 'model/Summary';
import {
  fetchSimpleProjectCombine,
  fetchSimpleProjectCompare,
} from 'reduxStore/simpleProject/asyncActions';
import {
  fetchEngagementScoreCombine,
  fetchEngagementScoreCompare,
} from 'reduxStore/engagementDrivers/asyncActions';
import { fetchOverallCombine, fetchOverallCompare } from 'reduxStore/overallSurvey/asyncActions';
import { SummaryPulseComparedModel } from 'api/dto/new/SummaryPulseCompared.dto';

import { STORE_NAME, SummaryState } from './initialState';
import {
  fetchPulsesSummary,
  fetchSummaryPulseCombined,
  fetchSummaryPulseCompared,
  fetchSummaryPulseLifecycleCombined,
  fetchSummaryPulseLifecycleCompared,
  fetchSummaryPulseOnDemandCombined,
  fetchSummaryPulseOnDemandCompared,
} from './asyncActions';

export const summarySlice = createSlice({
  name: STORE_NAME,
  initialState: SummaryState.INITIAL_DOMAIN,

  reducers: {
    clearSummary: (state) => {
      state.combine = SummaryState.INITIAL_DOMAIN_COMBINE as Draft<Resource<Summary.Combine>>;
      state.compare = SummaryState.INITIAL_DOMAIN_COMPARE as Draft<Resource<Summary.Compare[]>>;
      state.pulses = SummaryState.INITIAL_DOMAIN_PULSES as Draft<Resource<Summary.Pulses>>;
      state.pulseCompared = SummaryState.INITIAL_DOMAIN_PULSE_COMPARED as Draft<
        Resource<SummaryPulseComparedModel | null>
      >;
    },
  },
  extraReducers: (builder) => {
    addAsyncCases({
      builder,
      asyncActionCreator: fetchSummaryPulseCompared,
      skipEmptyFulfilled: true,
      reducer: (state, action) => {
        state.pulseCompared = action.payload as Draft<Resource<SummaryPulseComparedModel>>;
      },
    });

    addAsyncCases({
      builder,
      asyncActionCreator: fetchPulsesSummary,
      skipEmptyFulfilled: true,
      reducer: (state, action) => {
        state.pulses = action.payload as Draft<Resource<Summary.Pulses>>;
      },
      initialState: SummaryState.INITIAL_DOMAIN_PULSES.data,
    });

    builder
      .addMatcher(
        (action) =>
          [
            fetchSimpleProjectCombine.fulfilled.type,
            fetchEngagementScoreCombine.fulfilled.type,
            fetchSummaryPulseCombined.fulfilled.type,
            fetchOverallCombine.fulfilled.type,
            fetchSummaryPulseOnDemandCombined.fulfilled.type,
            fetchSummaryPulseLifecycleCombined.fulfilled.type,
          ].includes(action.type),
        (state, action) => {
          if (action.payload?.summary) {
            state.combine = Resource.createResolvedResource({ data: action.payload.summary });
          }
        }
      )
      .addMatcher(
        (action) =>
          [
            fetchSimpleProjectCompare.fulfilled.type,
            fetchEngagementScoreCompare.fulfilled.type,
            fetchOverallCompare.fulfilled.type,
            fetchSummaryPulseOnDemandCompared.fulfilled.type,
            fetchSummaryPulseLifecycleCompared.fulfilled.type,
          ].includes(action.type),
        (state, action) => {
          if (action.payload?.summary) {
            state.compare = Resource.createResolvedResource({ data: action.payload.summary });
          }
        }
      )
      .addMatcher(fetchSummaryPulseCombined.rejected.match, (state, action) => {
        state.combine = Resource.createRejectedResource({
          error: action.payload ?? action.error,
        }) as Draft<Resource<Summary.Combine>>;
      });
  },
});

export const { clearSummary } = summarySlice.actions;
